import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { BrandLogo } from "@/components/Brand";
import { Check, Loader2, LogOut } from "lucide-react";

const FEATURES = [
  "Content Command Center, a full week of platform-ready content from one theme",
  "AI Business Guide grounded in your brand and business setup",
  "Brand Center, Idea Vault, Projects & Knowledge Library",
  "SOP Library, Planning and Business Center financials",
  "Your own private, isolated workspace. Data always exportable",
];

export default function Billing() {
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  // Self-heal: if this customer has already paid (Stripe shows an active subscription) but the webhook
  // didn't sync it, /billing/status reconciles from Stripe. If access is granted, take them into the app.
  useEffect(() => {
    let active = true;
    api.get("/billing/status")
      .then(async (r) => {
        if (active && r.data?.has_access) {
          await checkAuth();
          navigate("/dashboard", { replace: true });
        }
      })
      .catch(() => {});
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subscribe = async () => {
    setBusy(true);
    try {
      const res = await api.post("/billing/checkout", { origin_url: window.location.origin });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error("Could not start checkout. Please try again.");
        setBusy(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
      setBusy(false);
    }
  };

  const inactive = user?.subscription_status && !["active", "trialing", "none"].includes(user.subscription_status);

  return (
    <div className="min-h-screen bg-background flex flex-col items-center px-6 py-14" data-testid="billing-page">
      <div className="w-full max-w-lg">
        <div className="flex items-center justify-between mb-10">
          <BrandLogo className="h-12 w-auto max-w-[220px] object-contain object-left" />
          <button onClick={logout} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5"
            data-testid="billing-logout-button">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>

        {inactive ? (
          <div className="mb-8 rounded-2xl bg-destructive/10 text-destructive px-5 py-4 text-sm" data-testid="billing-inactive-notice">
            Your subscription is currently <span className="font-semibold">{user.subscription_status}</span>.
            Renew below to restore access to your workspace.
          </div>
        ) : (
          <div className="mb-8" data-testid="billing-intro">
            <h1 className="font-heading text-3xl sm:text-4xl text-foreground mb-2">Start your workspace</h1>
            <p className="text-muted-foreground leading-relaxed">
              Work &amp; Keep is your calm, private command center. One simple plan gets you everything.
            </p>
          </div>
        )}

        <Card className="rounded-3xl border-border shadow-soft p-8" data-testid="billing-plan-card">
          <p className="text-sm font-medium text-primary uppercase tracking-wide">Work &amp; Keep</p>
          <div className="mt-3 flex items-end gap-2">
            <span className="font-heading text-5xl text-foreground" data-testid="plan-monthly-price">$70</span>
            <span className="text-muted-foreground mb-2">/ month</span>
          </div>
          <p className="text-sm text-muted-foreground mt-2" data-testid="plan-setup-fee">
            + a one-time <span className="font-semibold text-foreground">$149</span> setup &amp; onboarding fee.
          </p>

          <div className="h-px bg-border my-6" />

          <ul className="space-y-3">
            {FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-3 text-sm">
                <span className="mt-0.5 h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <Check className="h-3 w-3 text-primary" />
                </span>
                <span className="text-foreground/90 leading-relaxed">{f}</span>
              </li>
            ))}
          </ul>

          <div className="mt-8 rounded-2xl border border-border bg-muted/60 px-5 py-4 text-xs text-foreground/70 leading-relaxed" data-testid="recurring-disclosure">
            $219 due today, which includes a $149 one time setup fee and the first $70 monthly subscription payment.
            Your Work &amp; Keep subscription continues at $70 per month until canceled. Cancel before your next billing
            date to prevent the next recurring charge. Cancellation is available through your billing portal. See {" "}
            <Link to="/terms" className="underline hover:text-foreground">Terms of Service</Link> for complete refund and
            cancellation terms.
          </div>

          <Button onClick={subscribe} disabled={busy} data-testid="subscribe-button"
            className="w-full rounded-full h-12 mt-4 bg-primary hover:bg-primary/90 text-primary-foreground text-base gap-2">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {inactive ? "Renew subscription" : "Subscribe & continue"}
          </Button>
          <p className="text-xs text-muted-foreground text-center mt-4">
            Secure checkout via Stripe. Cancel anytime from Settings &rarr; Manage subscription.
          </p>
        </Card>

        <p className="text-xs text-muted-foreground text-center mt-6">
          Signed in as {user?.email}
        </p>
      </div>
    </div>
  );
}
